import { Dropdown, Modal } from 'bootstrap';
import { GpxRouteApi, IGpxRouteListItem, IGpxTagCount, IGpxSortDescriptor } from './GpxRouteApi';
import { GpxRouteStore } from './GpxRouteStore';
import { GpxPanelState } from './GpxPanelState';
import TrailwiseEventBus from '../TrailwiseEventBus';
import {
    escapeHtml,
    escapeAttribute,
    normaliseColour,
    normaliseShareUrl,
    copyTextToClipboard,
    getModalById
} from './MapGpxUtils';
import * as olProj from 'ol/proj';
import * as olExtent from 'ol/extent';

/** How long a revealed route's card keeps its pulse ring. */
const ROUTE_HIGHLIGHT_MS = 6000;

export interface IGpxPanelCallbacks {
    zoomToRoute: (gpxGuid: string) => void;
    setRouteVisibility: (gpxGuid: string, visible: boolean) => Promise<void>;
    /** Re-request the map's route geometry (after a colour/delete change). */
    refreshMapRoutes: () => void;
    /** The search/tag filter changed — the map layers should re-query to match. */
    onFilterCriteriaChanged: () => void;
}

/**
 * The GPX routes slide-panel tab: server-paged route list with search, tag
 * filter (ANY match), two-level sorting and paging, plus the per-route actions
 * (colour/zoom/visibility/share/delete/edit) and the modals they use.
 *
 * The paged list is a server query view; GpxRouteStore remains the map-side
 * cache of every route (fed by the sidebar API + the /features geometry layer).
 * Rendered rows merge live store values (visibility, colour) by guid, and list
 * extents are merged into the store so zoom-to-route works for routes whose
 * geometry hasn't loaded yet.
 */
export class GpxPanel {

    private deleteModal: Modal | null = null;
    private deleteModalElement?: HTMLElement;
    private pendingDeleteGpxGuid: string | null = null;
    private shareFeedbackTimer: number | null = null;
    private shareFeedbackElement: HTMLElement | null = null;
    private routeHighlightTimer: number | null = null;
    /** Guids of the currently-expanded route cards, preserved across list rebuilds. */
    private readonly expandedGuids = new Set<string>();
    /** False for anonymous users, where the panel markup isn't rendered at all. */
    private panelPresent = false;
    /**
     * Reveal state. All three are single-valued, so two reveals in quick succession
     * are simply last-one-wins. `highlightGuid` is render state (re-applied on every
     * rebuild, like expandedGuids); `revealGuid` is a one-shot "scroll to this on the
     * next render"; `pendingFocusGuid` is handed to the next list query so the server
     * can pick the page holding the route.
     */
    private highlightGuid: string | null = null;
    private revealGuid: string | null = null;
    private pendingFocusGuid: string | null = null;
    private errorModal: Modal | null = null;
    private errorMessageElement: HTMLElement | null = null;

    private lastItems: IGpxRouteListItem[] = [];
    private totalCount = 0;
    private listLoaded = false;
    private listLoadError = false;
    private queryVersion = 0;
    private searchDebounceTimer: number | null = null;
    private heightTimer: number | null = null;
    private pendingTags: string[] = [];
    private tagDropdown: Dropdown | null = null;
    private tagChipsVersion = 0;
    private editEnabled = false;

    public constructor(
        private store: GpxRouteStore,
        private api: GpxRouteApi,
        private defaultColour: string,
        private panelState: GpxPanelState,
        private callbacks: IGpxPanelCallbacks
    ) {
        if (!document.getElementById('gpxFiles')) {
            //anonymous users get the sign-in prompt instead of the panel
            return;
        }
        this.panelPresent = true;

        this.initDeleteModal();
        this.initShareFeedback();
        this.initErrorModal();
        this.initFilterControls();

        TrailwiseEventBus.gpxRoutesChanged.on(() => {
            void this.queryList();
        });

        //keep the routes list sized so the panel reaches the map bottom, not the footer.
        //recompute when the tab opens, when the window resizes, and when the filter card
        //expands/collapses (which moves where the list starts).
        TrailwiseEventBus.slidePanelShown.on((action) => {
            if (action === 'gpx') {
                window.setTimeout(() => this.adjustListHeight(), 50);
            }
        });
        $('#gpxFilterSection').on('shown.bs.collapse hidden.bs.collapse', () => this.adjustListHeight());
        window.addEventListener('resize', () => {
            if (this.heightTimer !== null) {
                window.clearTimeout(this.heightTimer);
            }
            this.heightTimer = window.setTimeout(() => this.adjustListHeight(), 150);
        });

        void this.queryList();
    }

    // -------------------------------------------------------------------------
    // Public API used by the orchestrator
    // -------------------------------------------------------------------------

    /** Enables the edit/draw-new actions (switched on once the edit session ships). */
    public setEditEnabled(enabled: boolean): void {
        this.editEnabled = enabled;
        $('#gpxDrawNewRoute').toggleClass('d-none', !enabled);
        this.renderRows();
    }

    /**
     * Re-renders the current page from cached items + live store state.
     * Called by the feature stream's coalesced rebuild — no server round-trip.
     */
    public buildGpxUi = (): void => {
        this.renderRows();
    }

    public showErrorModal(message: string): void {
        if (!this.errorModal || !this.errorMessageElement) {
            return;
        }
        this.errorMessageElement.textContent = message;
        this.errorModal.show();
    }

    /**
     * Brings a route's card into view: expands it, scrolls to it and pulses it —
     * switching to whichever page holds it if it isn't on the one being shown.
     *
     * The reveal is registered as state and consumed by renderRows rather than being
     * applied to the DOM here, because renderRows is the single funnel every rebuild
     * goes through and it empties the container each time. Applying it directly would
     * race with the rebuild that a concurrent query, a colour change or the feature
     * layer's batched refresh can trigger at any moment.
     */
    public async revealRoute(gpxGuid: string): Promise<void> {
        if (!this.panelPresent || !gpxGuid) {
            return;
        }
        const guid = gpxGuid.toLowerCase();

        this.revealGuid = guid;
        this.setHighlight(guid);
        this.expandedGuids.add(guid);

        if (this.findRouteCard(guid)) {
            //already on this page — re-render to apply the expansion and highlight
            this.renderRows();
            return;
        }

        //the server knows the ordering, so let it pick the page holding the route
        this.pendingFocusGuid = guid;
        await this.queryList();
    }

    private findRouteCard(gpxGuid: string): HTMLElement | null {
        return document.querySelector(
            `#gpxFiles .gpx-route-card[data-gpx-guid="${gpxGuid}"]`
        ) as HTMLElement | null;
    }

    /**
     * Sets (or clears) the pulsed card. Clearing goes through the live DOM rather
     * than a captured element: the card the highlight was applied to is thrown away
     * by the next list rebuild, so a captured reference would leave the highlight
     * stuck on the card that replaced it.
     */
    private setHighlight(gpxGuid: string | null): void {
        if (this.routeHighlightTimer !== null) {
            window.clearTimeout(this.routeHighlightTimer);
            this.routeHighlightTimer = null;
        }
        document.querySelectorAll('#gpxFiles .gpx-route-card-highlight')
            .forEach(card => card.classList.remove('gpx-route-card-highlight'));

        this.highlightGuid = gpxGuid;
        if (!gpxGuid) {
            return;
        }
        this.routeHighlightTimer = window.setTimeout(() => this.setHighlight(null), ROUTE_HIGHLIGHT_MS);
    }

    /** Scrolls to a pending reveal once the list that should contain it has rendered. */
    private consumeReveal(): void {
        const guid = this.revealGuid;
        if (!guid) {
            return;
        }
        //one shot either way: a reveal that missed its page must not fire later
        //against an unrelated rebuild
        this.revealGuid = null;
        this.findRouteCard(guid)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    public showShareModal(message: string): void {
        const modalElement = document.getElementById('gpxShareSuccessModal');
        if (!modalElement) {
            return;
        }
        const messageElement = document.getElementById('gpxShareSuccessMessage');
        if (messageElement && message) {
            messageElement.textContent = message;
        }
        if (modalElement.parentElement !== document.body) {
            document.body.appendChild(modalElement);
        }
        Modal.getOrCreateInstance(modalElement).show();
    }

    // -------------------------------------------------------------------------
    // Initialisation
    // -------------------------------------------------------------------------

    private initDeleteModal(): void {
        const modal = getModalById('gpxDeleteModal');
        this.deleteModalElement = modal.element;
        this.deleteModal = modal.instance;
        const confirmButton = document.getElementById('gpxDeleteConfirmButton') as HTMLButtonElement;
        confirmButton.addEventListener('click', () => {
            void this.executeDeleteRoute();
        });
        this.deleteModalElement.addEventListener('hidden.bs.modal', () => {
            this.pendingDeleteGpxGuid = null;
        });
    }

    private initShareFeedback(): void {
        const container = document.getElementById('gpxShareFeedback') as HTMLElement;
        this.shareFeedbackElement = container;
        container.addEventListener('close.bs.alert', (event) => {
            event.preventDefault();
            container.classList.add('d-none');
            container.classList.remove('show');
            if (this.shareFeedbackTimer !== null) {
                window.clearTimeout(this.shareFeedbackTimer);
                this.shareFeedbackTimer = null;
            }
        });
    }

    private initErrorModal(): void {
        const modal = getModalById('gpxErrorModal');
        this.errorModal = modal.instance;
        this.errorMessageElement = document.getElementById('gpxErrorMessage');
    }

    private initFilterControls(): void {
        const self = this;
        const state = this.panelState.state;

        //collapsible search & filter card — Bootstrap's collapse data API does the
        //toggling; we only persist the open state and swap the chevron
        const filterSection = document.getElementById('gpxFilterSection');
        if (filterSection) {
            if (state.filtersOpen) {
                //applied as classes rather than Collapse.show() so the remembered
                //open state doesn't animate on first paint
                filterSection.classList.add('show');
                $('#gpxFilterToggle').removeClass('collapsed').attr('aria-expanded', 'true');
            }
            this.setFilterChevron(state.filtersOpen);
            filterSection.addEventListener('shown.bs.collapse', function () {
                self.panelState.update({ filtersOpen: true });
                self.setFilterChevron(true);
            });
            filterSection.addEventListener('hidden.bs.collapse', function () {
                self.panelState.update({ filtersOpen: false });
                self.setFilterChevron(false);
            });
        }

        //search (debounced; magnifying-glass commits immediately) + clear
        const searchInput = $('#gpxSearch');
        searchInput.val(state.search);
        this.updateSearchClearVisibility();
        searchInput.on('input', function () {
            if (self.searchDebounceTimer !== null) {
                window.clearTimeout(self.searchDebounceTimer);
            }
            self.searchDebounceTimer = window.setTimeout(() => {
                self.searchDebounceTimer = null;
                self.commitSearch();
            }, 300);
        });
        searchInput.on('keydown', function (event) {
            if (event.key === 'Enter') {
                event.preventDefault();
                self.commitSearch();
            }
        });
        $('#gpxSearchBtn').on('click', function () {
            self.commitSearch();
        });
        $('#gpxSearchClear').on('click', function () {
            searchInput.val('');
            self.commitSearch();
        });

        //tag filter: Bootstrap dropdown with autoClose="outside" so chip clicks keep
        //it open. Closing (Apply, Clear or an outside click) commits the selection.
        const tagButton = document.getElementById('gpxTagFilterBtn');
        if (tagButton) {
            this.tagDropdown = Dropdown.getOrCreateInstance(tagButton);
            tagButton.addEventListener('show.bs.dropdown', function () {
                void self.loadTagChips();
            });
            tagButton.addEventListener('hidden.bs.dropdown', function () {
                self.applyPendingTags();
            });
        }
        $('#gpxTagFilterApply').on('click', function () {
            self.tagDropdown?.hide();
        });
        $('#gpxTagFilterClear').on('click', function () {
            self.pendingTags = [];
            self.tagDropdown?.hide();
        });

        //sort
        this.applySortStateToControls();
        $('#gpxSort1Field').on('change', function () {
            self.readSortFromControls();
        });
        $('#gpxSort1Dir').on('click', function () {
            $(this).data('desc', !$(this).data('desc'));
            self.updateSortDirIcon(<HTMLElement>this);
            self.readSortFromControls();
        });

        //page size + paging
        $('#gpxPageSize').val(state.pageSize.toString()).on('change', function () {
            self.panelState.update({ pageSize: parseInt(<string>$(this).val(), 10) || 15, page: 1 });
            void self.queryList();
        });
        $('#gpxPrevPage').on('click', function () {
            if (self.panelState.state.page > 1) {
                self.panelState.update({ page: self.panelState.state.page - 1 });
                void self.queryList();
            }
        });
        $('#gpxNextPage').on('click', function () {
            const maxPage = Math.max(1, Math.ceil(self.totalCount / self.panelState.state.pageSize));
            if (self.panelState.state.page < maxPage) {
                self.panelState.update({ page: self.panelState.state.page + 1 });
                void self.queryList();
            }
        });

        //display switches in the settings menu — clicking one keeps the menu open
        //(Bootstrap closes on any dropdown-item click, including the label)
        $('#gpxShowNotes').prop('checked', state.showNotes).on('change', function () {
            self.panelState.update({ showNotes: $(this).is(':checked') });
            self.renderRows();
        });
        $('#gpxShowTags').prop('checked', state.showTags).on('change', function () {
            self.panelState.update({ showTags: $(this).is(':checked') });
            self.renderRows();
        });
        $('#gpxDisplayOptions').on('click', function (event) {
            event.stopPropagation();
        });

        //draw new route → edit session (enabled via setEditEnabled)
        $('#gpxDrawNewRoute').on('click', function () {
            TrailwiseEventBus.gpxEditRequested({ gpxGuid: null });
        });

        this.updateTagButtonState();
        this.updateFilterBadge();
    }

    /** Commits the current search input value (flushing any pending debounce). */
    private commitSearch(): void {
        if (this.searchDebounceTimer !== null) {
            window.clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = null;
        }
        this.panelState.update({ search: (<string>$('#gpxSearch').val() ?? '').trim(), page: 1 });
        this.updateSearchClearVisibility();
        void this.queryList();
        this.callbacks.onFilterCriteriaChanged();
    }

    private setFilterChevron(open: boolean): void {
        $('#gpxFilterChevron')
            .toggleClass('fa-chevron-up', open)
            .toggleClass('fa-chevron-down', !open);
    }

    /** Flags a search or tag filter while the section is collapsed (sorting doesn't count). */
    private updateFilterBadge(): void {
        const state = this.panelState.state;
        const filtered = !!state.search || state.tags.length > 0;
        $('#gpxFilterBadge').toggleClass('d-none', !filtered);
    }

    // -------------------------------------------------------------------------
    // Sort controls
    // -------------------------------------------------------------------------

    private applySortStateToControls(): void {
        const sort = this.panelState.state.sort;
        $('#gpxSort1Field').val(sort.field);
        $('#gpxSort1Dir').data('desc', sort.descending);
        this.updateSortDirIcon(document.getElementById('gpxSort1Dir'));
    }

    private readSortFromControls(): void {
        const sort: IGpxSortDescriptor = {
            field: <any>$('#gpxSort1Field').val(),
            descending: !!$('#gpxSort1Dir').data('desc')
        };
        this.panelState.update({ sort: sort, page: 1 });
        void this.queryList();
    }

    private updateSortDirIcon(button: HTMLElement | null): void {
        if (!button) {
            return;
        }
        const desc = !!$(button).data('desc');
        const icon = button.querySelector('i');
        if (icon) {
            icon.className = desc ? 'fas fa-arrow-down-z-a' : 'fas fa-arrow-up-a-z';
        }
        button.title = desc ? 'Descending — click for ascending' : 'Ascending — click for descending';
    }

    // -------------------------------------------------------------------------
    // Tag filter
    // -------------------------------------------------------------------------

    /** Fills the dropdown with tag chips (pill buttons + count badges) on each open. */
    private async loadTagChips(): Promise<void> {
        const version = ++this.tagChipsVersion;
        this.pendingTags = [...this.panelState.state.tags];

        let tags: IGpxTagCount[] = [];
        try {
            tags = await this.api.getTagSummary();
        } catch (error) {
            console.error('Failed to load tag summary', error);
        }

        if (version !== this.tagChipsVersion) {
            return; //a later open superseded this load
        }

        const chips = $('#gpxTagFilterChips');
        chips.empty();
        if (tags.length === 0) {
            chips.append('<span class="text-muted small">No tags yet — add tags to your routes to filter by them.</span>');
            return;
        }

        const self = this;
        for (const tag of tags) {
            const selected = this.pendingTags.indexOf(tag.tag) >= 0;
            const chip = $(`<button type="button" class="btn btn-sm rounded-pill position-relative text-capitalize ${selected ? 'btn-warning' : 'btn-outline-secondary'}" data-tag="${escapeAttribute(tag.tag)}">${escapeHtml(tag.tag)}<span class="badge rounded-pill bg-secondary border border-white position-absolute top-0 start-100 translate-middle">${tag.count}</span></button>`);
            chip.on('click', function () {
                //attr() not data(): jQuery's .data() coerces attribute values, so a
                //numeric tag like "123" would come back as the number 123
                const value = $(this).attr('data-tag');
                if (!value) {
                    return;
                }
                const index = self.pendingTags.indexOf(value);
                if (index >= 0) {
                    self.pendingTags.splice(index, 1);
                } else {
                    self.pendingTags.push(value);
                }
                const nowSelected = index < 0;
                $(this)
                    .toggleClass('btn-warning', nowSelected)
                    .toggleClass('btn-outline-secondary', !nowSelected);
            });
            chips.append(chip);
        }
    }

    /** Commits the dropdown's pending selection (no-op when nothing changed). */
    private applyPendingTags(): void {
        const current = this.panelState.state.tags;
        const next = [...this.pendingTags];
        if (next.length === current.length && next.every((tag, index) => tag === current[index])) {
            return;
        }
        this.panelState.update({ tags: next, page: 1 });
        this.updateTagButtonState();
        void this.queryList();
        this.callbacks.onFilterCriteriaChanged();
    }

    /** The Tags button turns amber while a tag filter is applied. */
    private updateTagButtonState(): void {
        const tags = this.panelState.state.tags;
        const hasTags = tags.length > 0;
        $('#gpxTagFilterBtn')
            .toggleClass('btn-warning', hasTags)
            .toggleClass('btn-outline-secondary', !hasTags)
            .attr('title', hasTags ? `Filtering by: ${tags.join(', ')}` : 'Filter by tags');
    }

    private toggleTagIntoFilter(tag: string | undefined): void {
        if (!tag) {
            return;
        }
        const tags = [...this.panelState.state.tags];
        const index = tags.indexOf(tag);
        if (index >= 0) {
            tags.splice(index, 1);
        } else {
            tags.push(tag);
        }
        this.panelState.update({ tags: tags, page: 1 });
        this.pendingTags = [...tags];
        this.updateTagButtonState();
        void this.queryList();
        this.callbacks.onFilterCriteriaChanged();
    }

    private updateSearchClearVisibility(): void {
        $('#gpxSearchClear').toggleClass('d-none', !this.panelState.state.search);
    }

    // -------------------------------------------------------------------------
    // Server query + rendering
    // -------------------------------------------------------------------------

    private async queryList(): Promise<void> {
        const version = ++this.queryVersion;
        //claimed synchronously alongside the version stamp so two interleaved queries
        //can't both act on the same reveal
        const focusGuid = this.pendingFocusGuid;
        this.pendingFocusGuid = null;
        const state = this.panelState.state;
        this.updateFilterBadge();

        try {
            const result = await this.api.getRouteList({
                search: state.search || undefined,
                tags: state.tags.length > 0 ? state.tags : undefined,
                page: state.page,
                pageSize: state.pageSize,
                sort: [state.sort],
                focusGuid: focusGuid ?? undefined
            });

            if (version !== this.queryVersion) {
                //stale response. Hand any focus request back so the next query still
                //lands on the right page rather than silently losing the reveal
                if (focusGuid && this.pendingFocusGuid === null) {
                    this.pendingFocusGuid = focusGuid;
                }
                return;
            }

            //the served page is authoritative: with focusGuid the server chose it
            if (result.page && result.page !== state.page) {
                this.panelState.update({ page: result.page });
            }

            //auto-clamp when the result set shrank below the current page
            if (result.items.length === 0 && result.totalCount > 0 && this.panelState.state.page > 1) {
                this.panelState.update({ page: Math.max(1, Math.ceil(result.totalCount / state.pageSize)) });
                void this.queryList();
                return;
            }

            this.lastItems = result.items;
            this.totalCount = result.totalCount;
            this.listLoaded = true;
            this.listLoadError = false;
            this.mergeItemsIntoStore(result.items);
            this.renderRows();
            this.renderPaging();
        } catch (error) {
            console.error('Failed to load GPX route list', error);
            if (version === this.queryVersion) {
                this.listLoaded = true;
                this.listLoadError = true;
                this.lastItems = [];
                this.totalCount = 0;
                this.renderRows();
                this.renderPaging();
            }
        }
    }

    /**
     * Keeps the map-side store's metadata in sync with the freshest server
     * values so row actions (zoom/visibility/colour) work for routes whose
     * geometry hasn't streamed in from the /features endpoint yet.
     */
    private mergeItemsIntoStore(items: IGpxRouteListItem[]): void {
        for (const item of items) {
            const colour = normaliseColour(item.colourHex || this.defaultColour, this.defaultColour);
            const extent = this.extentFromItem(item);
            const existing = this.store.getMutable(item.gpxGuid);
            if (existing) {
                existing.displayName = item.displayName;
                existing.notes = item.notes ?? null;
                existing.colourHex = colour;
                existing.lengthMetres = item.lengthMetres;
                existing.isVisible = item.isVisible ?? true;
                if (!existing.extent && extent) {
                    existing.extent = extent;
                }
            } else {
                this.store.upsertSilent({
                    gpxGuid: item.gpxGuid,
                    displayName: item.displayName,
                    notes: item.notes ?? null,
                    colourHex: colour,
                    features: [],
                    extent: extent,
                    lengthMetres: item.lengthMetres,
                    originalFileName: undefined,
                    isVisible: item.isVisible ?? true
                });
            }
        }
    }

    private extentFromItem(item: IGpxRouteListItem): olExtent.Extent | null {
        if (item.extentMinX == null || item.extentMinY == null || item.extentMaxX == null || item.extentMaxY == null) {
            return null;
        }
        const min = olProj.fromLonLat([item.extentMinX, item.extentMinY]);
        const max = olProj.fromLonLat([item.extentMaxX, item.extentMaxY]);
        return [min[0], min[1], max[0], max[1]] as olExtent.Extent;
    }

    private renderRows(): void {
        const self = this;
        const container = $('#gpxFiles');
        if (!container.length) {
            return;
        }
        container.empty();

        if (!this.listLoaded) {
            container.append(`
                <div class="d-flex align-items-center text-muted small py-1" data-gpx-loading>
                    <div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                    Loading routes&hellip;
                </div>`);
            return;
        }

        if (this.listLoadError) {
            container.append(`<p class='text-danger small mb-0' data-gpx-list-error>Couldn't load your routes. Please try again.</p>`);
        } else if (this.lastItems.length === 0) {
            const hasFilters = !!this.panelState.state.search || this.panelState.state.tags.length > 0;
            container.append(`<p class='text-muted small mb-0' data-gpx-empty-state>${hasFilters ? 'No routes match the current filters.' : 'No saved GPX routes yet.'}</p>`);
        } else {
            const markup: string[] = [];
            for (let index = 0; index < this.lastItems.length; index++) {
                const item = this.lastItems[index];

                //live store values win for the mutable fields
                const record = this.store.get(item.gpxGuid);
                const colour = record?.colourHex ?? normaliseColour(item.colourHex || this.defaultColour, this.defaultColour);
                const isVisible = record?.isVisible ?? item.isVisible ?? true;
                const lengthMetres = record?.lengthMetres ?? item.lengthMetres;

                const lengthLabel = lengthMetres
                    ? `${(lengthMetres / 1000).toFixed(2)} km`
                    : '';
                //created, not updated: for an imported route this is the date carried in
                //the GPX file, and unlike UpdatedUtc it isn't re-stamped by later edits
                const dateLabel = this.formatDate(item.createdUtc);
                const metaLine = [lengthLabel, dateLabel].filter(Boolean).join(' · ');
                const displayName = escapeHtml(item.displayName);
                const notesBody = (item.notes && this.panelState.state.showNotes)
                    ? `<div class="small mb-2 gpx-route-notes-body">${escapeHtml(item.notes).replace(/\r?\n/g, '<br>')}</div>`
                    : '';
                const notesAttr = escapeAttribute(item.notes);
                const visibilityTitle = isVisible ? 'Hide route' : 'Show route';
                const visibilityBtnIcon = isVisible ? 'fa-eye-slash' : 'fa-eye';
                const hiddenIndicator = isVisible
                    ? ''
                    : ' <i class="fas fa-eye-slash text-muted" title="Hidden"></i>';

                const tagBadges = (item.tags && item.tags.length > 0 && this.panelState.state.showTags)
                    ? `<div class="gpx-route-tags mb-2">${item.tags.map(t =>
                        `<span class="badge rounded-pill bg-light text-dark gpx-route-tag" data-gpx-tag="${escapeAttribute(t)}" role="button" title="Filter by ${escapeAttribute(t)}">${escapeHtml(t)}</span>`).join(' ')}</div>`
                    : '';

                const editButton = this.editEnabled
                    ? `<button type="button" class="btn btn-outline-secondary" data-gpx-action="edit" data-gpx-guid="${item.gpxGuid}" title="Edit ${displayName}" aria-label="Edit ${displayName}">
                            <i class="fas fa-pen"></i>
                        </button>`
                    : '';

                const bodyId = `gpxRouteBody-${item.gpxGuid}`;
                const isExpanded = this.expandedGuids.has(item.gpxGuid);
                //render state, not a one-off DOM tweak — otherwise the next rebuild
                //(colour change, visibility toggle, batched feature refresh) drops it
                const isHighlighted = this.highlightGuid === item.gpxGuid.toLowerCase();

                markup.push(`
                    <div class="card gpx-route-card mb-2${isHighlighted ? ' gpx-route-card-highlight' : ''}" data-gpx-guid="${item.gpxGuid}">
                        <div class="card-header p-0">
                            <button type="button" class="btn btn-link text-reset text-decoration-none d-flex align-items-center w-100 px-2 py-2 gpx-route-toggle${isExpanded ? '' : ' collapsed'}"
                                    data-bs-toggle="collapse" data-bs-target="#${bodyId}" aria-expanded="${isExpanded}" aria-controls="${bodyId}">
                                <span class="flex-grow-1 text-start">
                                    <span class="fw-bold d-block gpx-route-name" data-gpx-route-name="${displayName}">${displayName}${hiddenIndicator}</span>
                                    ${metaLine ? `<span class="d-block small gpx-route-meta"${lengthMetres ? ` data-gpx-length="${lengthMetres}"` : ''}>${metaLine}</span>` : ''}
                                </span>
                                <span class="gpx-route-colour-indicator mx-2" style="border-top-color:${colour}" data-gpx-colour-indicator title="Route colour"></span>
                                <i class="fas fa-chevron-down gpx-route-chevron" aria-hidden="true"></i>
                            </button>
                        </div>
                        <div id="${bodyId}" class="collapse gpx-route-collapse${isExpanded ? ' show' : ''}">
                            <div class="card-body">
                                ${notesBody}
                                ${tagBadges}
                                <div class="gpx-route-entry d-flex align-items-center flex-wrap gap-1" data-gpx-guid="${item.gpxGuid}" data-gpx-name="${displayName}" data-gpx-colour="${colour}" data-gpx-notes="${notesAttr}">
                                    <input type="color" class="form-control form-control-color form-control-sm" value="${colour}" data-gpx-action="colour" data-gpx-guid="${item.gpxGuid}" aria-label="Choose a colour for ${displayName}">
                                    ${editButton}
                                    <button type="button" class="btn btn-outline-secondary" data-gpx-action="zoom" data-gpx-guid="${item.gpxGuid}" title="Zoom to ${displayName}" aria-label="Zoom to ${displayName}">
                                        <i class="fas fa-search-plus"></i>
                                    </button>
                                    <button type="button" class="btn btn-outline-secondary" data-gpx-action="visibility" data-gpx-guid="${item.gpxGuid}" title="${visibilityTitle}" aria-label="${visibilityTitle}">
                                        <i class="fas ${visibilityBtnIcon}"></i>
                                    </button>
                                    <a class="btn btn-outline-secondary" data-gpx-action="share" data-gpx-share-url="/gpxroutes/share/${item.gpxGuid}" href="/gpxroutes/share/${item.gpxGuid}" title="Share ${displayName}"><i class="fas fa-share-alt"></i></a>
                                    <button class="btn btn-outline-secondary" data-gpx-action="delete" data-gpx-guid="${item.gpxGuid}" title="Remove ${displayName}"><i class="fas fa-trash"></i></button>
                                </div>
                            </div>
                        </div>
                    </div>`);
            }
            container.append(markup.join(''));
        }

        container.find('[data-gpx-action]').off('click').on('click', function (event) {
            const action = $(this).data('gpxAction') as string;
            const gpxGuid = $(this).data('gpxGuid') as string;
            if (action === 'colour') {
                return;
            }
            if (action === 'share') {
                event.preventDefault();
                const shareUrl = ($(this).data('gpxShareUrl') as string | undefined) ?? $(this).attr('href');
                //pass the button so the confirmation can appear right above it
                void self.handleShareLinkClick(shareUrl, this as HTMLElement);
                return;
            }
            event.preventDefault();
            if (!gpxGuid) {
                return;
            }
            if (action === 'delete') {
                self.handleDelete(gpxGuid);
            } else if (action === 'zoom') {
                self.callbacks.zoomToRoute(gpxGuid);
            } else if (action === 'visibility') {
                self.handleVisibilityToggle(gpxGuid);
            } else if (action === 'edit') {
                TrailwiseEventBus.gpxEditRequested({ gpxGuid: gpxGuid });
            }
        });

        container.find('[data-gpx-action="colour"]').off('change').on('change', function () {
            const gpxGuid = $(this).data('gpxGuid') as string;
            const colour = $(this).val() as string;
            if (!gpxGuid || !colour) {
                return;
            }
            //reflect the new colour in the card header indicator immediately
            container.find(`.gpx-route-card[data-gpx-guid="${gpxGuid}"] [data-gpx-colour-indicator]`)
                .css('border-top-color', colour);
            void self.updateRouteColour(gpxGuid, colour, this as HTMLInputElement);
        });

        container.find('.gpx-route-tag').off('click').on('click', function () {
            //attr() not data() — see the tag chip handler: .data() would turn a
            //numeric tag into a number, which then fails to persist as a string
            self.toggleTagIntoFilter($(this).attr('data-gpx-tag'));
        });

        //track which cards are open so they survive the frequent list rebuilds
        //(visibility toggles, colour changes and store updates all re-render the list)
        container.find('.gpx-route-collapse')
            .off('shown.bs.collapse hidden.bs.collapse')
            .on('shown.bs.collapse', function () {
                self.expandedGuids.add($(this).closest('.gpx-route-card').data('gpxGuid') as string);
            })
            .on('hidden.bs.collapse', function () {
                self.expandedGuids.delete($(this).closest('.gpx-route-card').data('gpxGuid') as string);
            });

        this.adjustListHeight();
        //after adjustListHeight: it sets the list's max-height, and until it has,
        //#gpxFiles isn't the scroll container and scrollIntoView moves the page instead
        this.consumeReveal();
    }

    /**
     * Sizes the routes list so the panel grows to the bottom of the map but never
     * into the footer. A fixed max-height can't do this because the list's top
     * moves when the Search & filter card expands/collapses, so measure it live.
     */
    private adjustListHeight(): void {
        const list = document.getElementById('gpxFiles');
        const mapContainer = document.getElementById('mapContainer');
        if (!list || !mapContainer || !$('#slide-panel-gpx').is(':visible')) {
            return;
        }
        const mapBottom = mapContainer.getBoundingClientRect().bottom;
        const listTop = list.getBoundingClientRect().top;
        const paging = document.querySelector('#slide-panel-gpx .gpx-paging') as HTMLElement | null;
        const pagingHeight = paging ? paging.offsetHeight : 40;
        const available = mapBottom - listTop - pagingHeight - 20;
        list.style.maxHeight = `${Math.max(120, available)}px`;
    }

    /** Formats an ISO date for the card meta line, e.g. "28 Jul 2026". */
    private formatDate(iso?: string): string {
        if (!iso) {
            return '';
        }
        const date = new Date(iso);
        if (isNaN(date.getTime())) {
            return '';
        }
        return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    }

    private renderPaging(): void {
        const state = this.panelState.state;
        const start = this.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1;
        const end = Math.min(this.totalCount, state.page * state.pageSize);
        const maxPage = Math.max(1, Math.ceil(this.totalCount / state.pageSize));

        $('#gpxPageInfo').text(this.totalCount === 0 ? 'No routes' : `Showing ${start}–${end} of ${this.totalCount}`);
        $('#gpxPrevPage').prop('disabled', state.page <= 1);
        $('#gpxNextPage').prop('disabled', state.page >= maxPage);
    }

    // -------------------------------------------------------------------------
    // Colour update
    // -------------------------------------------------------------------------

    private async updateRouteColour(gpxGuid: string, colour: string, input: HTMLInputElement): Promise<void> {
        const normalised = normaliseColour(colour, this.defaultColour);
        try {
            const result = await this.api.updateRouteColour(gpxGuid, normalised);
            const record = this.store.getMutable(gpxGuid);
            if (record) {
                record.colourHex = normaliseColour(result.colourHex, this.defaultColour);
            }
            this.callbacks.refreshMapRoutes();
            this.renderRows();
        } catch (error) {
            console.error('Failed to update colour', error);
            const record = this.store.get(gpxGuid);
            if (record) {
                input.value = record.colourHex;
                $(`.gpx-route-card[data-gpx-guid="${gpxGuid}"] [data-gpx-colour-indicator]`)
                    .css('border-top-color', record.colourHex);
            }
        }
    }

    // -------------------------------------------------------------------------
    // Visibility
    // -------------------------------------------------------------------------

    private handleVisibilityToggle(gpxGuid: string): void {
        const record = this.store.get(gpxGuid);
        if (!record) {
            return;
        }
        void this.callbacks.setRouteVisibility(gpxGuid, !record.isVisible);
    }

    // -------------------------------------------------------------------------
    // Delete
    // -------------------------------------------------------------------------

    private handleDelete(gpxGuid: string): void {
        if (this.deleteModalElement.parentElement !== document.body) {
            document.body.appendChild(this.deleteModalElement);
        }
        const record = this.store.get(gpxGuid);
        const item = this.lastItems.find(i => i.gpxGuid === gpxGuid);
        const displayName = record?.displayName ?? item?.displayName;
        if (!displayName) {
            return;
        }
        const nameElement = document.getElementById('gpxDeleteName');
        if (nameElement) {
            nameElement.textContent = displayName;
        }
        this.pendingDeleteGpxGuid = gpxGuid;
        this.deleteModal.show();
    }

    private async executeDeleteRoute(): Promise<void> {
        if (!this.pendingDeleteGpxGuid) {
            return;
        }
        const gpxGuid = this.pendingDeleteGpxGuid;
        this.pendingDeleteGpxGuid = null;
        const success = await this.performDelete(gpxGuid);
        this.deleteModal?.hide();
        if (!success) {
            this.showErrorModal('Unable to delete the route. Please try again.');
        }
    }

    private async performDelete(gpxGuid: string): Promise<boolean> {
        try {
            await this.api.deleteRoute(gpxGuid);
            this.store.remove(gpxGuid);
            this.callbacks.refreshMapRoutes();
            TrailwiseEventBus.gpxRoutesChanged(gpxGuid);
            return true;
        } catch (error) {
            console.error('Failed to delete GPX route', error);
            return false;
        }
    }

    // -------------------------------------------------------------------------
    // Share
    // -------------------------------------------------------------------------

    private async handleShareLinkClick(
        shareUrl: string | null | undefined,
        anchor: HTMLElement | null = null
    ): Promise<void> {
        if (!shareUrl) {
            this.showShareFeedback('We could not find the share link to copy.', true, anchor);
            return;
        }
        const absoluteUrl = normaliseShareUrl(shareUrl);
        const copied = await copyTextToClipboard(absoluteUrl);
        if (copied) {
            this.showShareFeedback('Link copied — paste it into an email to share this route with other TW2 members.', false, anchor);
            return;
        }
        this.showShareFeedback(
            `Unable to copy the share link automatically. Copy it manually to email it: ${absoluteUrl}`,
            true,
            anchor
        );
    }

    private showShareFeedback(message: string, isError: boolean = false, anchor: HTMLElement | null = null): void {
        const container = this.shareFeedbackElement;
        if (!container) {
            return;
        }
        const messageElement = document.getElementById('gpxShareFeedbackMessage') as HTMLElement;
        messageElement.textContent = message;
        container.classList.remove('d-none', 'alert-success', 'alert-danger', 'fade');
        container.classList.add(isError ? 'alert-danger' : 'alert-success', 'show');
        container.setAttribute('role', 'alert');
        container.style.removeProperty('display');

        //position only once it's visible — offsetHeight is 0 while d-none
        this.positionShareFeedback(container, anchor);

        if (this.shareFeedbackTimer !== null) {
            window.clearTimeout(this.shareFeedbackTimer);
        }
        this.shareFeedbackTimer = window.setTimeout(() => {
            container.classList.add('d-none');
            container.classList.remove('show');
        }, 8000);
    }

    /**
     * Floats the confirmation directly above the share button that was clicked, so
     * it reads as a response to that route rather than a panel-wide banner. Falls
     * back to the top of the panel when there's no anchor.
     */
    private positionShareFeedback(container: HTMLElement, anchor: HTMLElement | null): void {
        const parent = container.offsetParent as HTMLElement | null;
        if (!anchor || !parent) {
            container.style.removeProperty('top');
            return;
        }

        const parentRect = parent.getBoundingClientRect();
        const anchorRect = anchor.getBoundingClientRect();
        const gap = 6;
        const top = anchorRect.top - parentRect.top - container.offsetHeight - gap;

        //clamp so it never floats off the top of the panel
        container.style.top = `${Math.max(0, top)}px`;
    }
}
